import React, { useEffect, useState } from 'react';
import {
  useGetBackofficePostulationStatusQuery,
  useUpdateBackofficePostulationStatusMutation,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styles from './Postulation.module.scss';
import Text from 'common/components/Text/Text';
import { useFormik } from 'formik';
import { PostulationRequirement, CommonHash } from 'app/types';
import Select from 'react-select';

interface PostulationData {
  status: string | null;
  cv: CommonHash[] | null;
  englishProof: CommonHash[] | null;
  technicalTest: CommonHash[] | null;
  referenceLetter: CommonHash[] | null;
  transcripts: CommonHash[] | null;
  personalStatement: CommonHash[] | null;
  scholarshipLetter: CommonHash[] | null;
  degreeCertificate: CommonHash[] | null;
  universityFee: CommonHash[] | null;
  additionalDocuments: CommonHash[] | null;
  [key: string]: any;
}

const handleStatusNameForLabel = (status: string) => {
  switch (status) {
    case 'missing':
      return 'Missing';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'uploaded':
      return 'Uploaded';
    default:
      return 'Missing';
  }
};
const Postulation = () => {
  const { id } = useParams<{ id: string }>();
  const { data: postulation, isLoading } = useGetBackofficePostulationStatusQuery(id || '');
  const [isInitialValuesLoading, setIsInitialValuesLoading] = useState(true);
  const [updatePostulationStatus] = useUpdateBackofficePostulationStatusMutation();
  const [clickedButtonLeave, setClickedButtonLeave] = useState(false);
  const navigate = useNavigate();

  const getDocuments = (name: string) => {
    return (
      postulation?.data.requirements
        ?.find((r) => r.name === name)
        ?.documents.map((d) => ({ id: d.id, status: d.status })) || []
    );
  };

  const formik = useFormik<PostulationData>({
    initialValues: {
      status: postulation?.data.status || '',
      cv: getDocuments('CV'),
      englishProof: getDocuments('English proof'),
      technicalTest: getDocuments('Technical test'),
      referenceLetter: getDocuments('Reference letter'),
      transcripts: getDocuments('Transcripts'),
      personalStatement: getDocuments('Personal statement'),
      scholarshipLetter: getDocuments('Scholarship letter'),
      degreeCertificate: getDocuments('Degree certificate'),
      universityFee: getDocuments('University Fee'),
      additionalDocuments: getDocuments('Additional documents'),
    },
    onSubmit: async (postulationData: PostulationData) => {
      if (postulation) {
        updatePostulationStatus({
          id: postulation.data.id,
          data: postulationData,
        }).then(() => {
          if (clickedButtonLeave) {
            navigate('/backoffice/applications');
          }
        });
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue('status', postulation?.data.status || '');
    formik.setFieldValue('cv', getDocuments('CV'));
    formik.setFieldValue('englishProof', getDocuments('English proof'));
    formik.setFieldValue('technicalTest', getDocuments('Technical test'));
    formik.setFieldValue('referenceLetter', getDocuments('Reference letter'));
    formik.setFieldValue('transcripts', getDocuments('Transcripts'));
    formik.setFieldValue('personalStatement', getDocuments('Personal statement'));
    formik.setFieldValue('scholarshipLetter', getDocuments('Scholarship letter'));
    formik.setFieldValue('degreeCertificate', getDocuments('Degree certificate'));
    formik.setFieldValue('universityFee', getDocuments('University Fee'));
    formik.setFieldValue('additionalDocuments', getDocuments('Additional documents'));
    setIsInitialValuesLoading(false);
  }, [postulation]);
  if (isLoading || isInitialValuesLoading) return <Loader />;
  return (
    <div className={styles.postulation}>
      <div className={styles.header}>
        <Text size={24} weight={800}>
          Postulation
        </Text>
        <Link
          className={`${styles.submitButton} btn`}
          to={`/backoffice/users/${postulation?.data.user.id}`}
        >
          Go to user profile
        </Link>
      </div>
      <div className={styles.options}>
        <div className={styles.element}>
          <label htmlFor="email">Email</label>
          <Text size={18} weight={400}>
            {postulation?.data.user.email}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">Program</label>
          <Text size={18} weight={400}>
            {postulation?.data.program.name}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">School</label>
          <Text size={18} weight={400}>
            {postulation?.data.program.school_name}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">City</label>
          <Text size={18} weight={400}>
            {postulation?.data.study_option.city?.name}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">Delivery</label>
          <Text size={18} weight={400}>
            {postulation?.data.study_option.delivery}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">Duration in months</label>
          <Text size={18} weight={400}>
            {postulation?.data.study_option.duration_in_months}
          </Text>
        </div>
        <div className={styles.element}>
          <label htmlFor="email">Schedule</label>
          <Text size={18} weight={400}>
            {postulation?.data.study_option.schedule}
          </Text>
        </div>
        <form
          className={styles.cardFlex}
          onSubmit={formik.handleSubmit}
          id="backofficePostulationForm"
        >
          {postulation?.data.requirements?.map((requirement: PostulationRequirement) => (
            <div key={requirement.id} className={styles.element}>
              <Text size={20} weight={600}>
                {requirement.name}
              </Text>
              {requirement.documents.map((document, index) => (
                <div key={document.id} className={styles.files}>
                  <div className={styles.firstRow}>
                    <Text size={14} weight={500}>
                      {`${document.name}_${index + 1}`}
                    </Text>
                    <i className="bi bi-download" onClick={() => window.open(document.url)}></i>
                  </div>
                  <div className={styles.secondRow}>
                    <Select
                      options={[
                        { label: 'Uploaded', value: 'uploaded' },
                        { label: 'Approved', value: 'approved' },
                        { label: 'Rejected', value: 'rejected' },
                      ]}
                      onChange={(e) => {
                        formik.setFieldValue(`${requirement.formName}[${index}]`, {
                          ...formik.values[requirement.formName][index],
                          status: e?.value,
                        });
                      }}
                      value={{
                        label: handleStatusNameForLabel(
                          formik.values[requirement.formName]?.[index]?.status
                        ),
                        value: formik.values[requirement.formName]?.[index]?.status,
                      }}
                      className={styles.select}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className={styles.buttonWrap}>
            <button type="submit" className={`${styles.submitButton} btn`}>
              Save
            </button>
            <button
              type="submit"
              className={`${styles.submitButton} btn ${styles.outline}`}
              onClick={() => setClickedButtonLeave(true)}
            >
              Save & Leave
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Postulation;
